import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addXP, loadXP } from '../data/xp';
import ModuleSummary from '../components/ModuleSummary';
import { recordStart } from '../data/progress';
import { syncProgress } from '../api/public';

// ─── Types ────────────────────────────────────────────────────────────────────

type MediaType = 'image' | 'audio' | 'video';

interface ArtifactMarker {
  top: string;
  left: string;
  label: string;
}

interface MediaPanel {
  id: 'A' | 'B';
  type: MediaType;
  title: string;
  description: string;
  isFake: boolean;
  artifactMarkers: ArtifactMarker[];
  visualNote?: string;
}

interface Clue {
  id: string;
  text: string;
  isCorrect: boolean;
}

interface Round {
  id: number;
  title: string;
  context: string;
  scenario: string;
  forensicConcept: string;
  forensicExplainer: string;
  panels: [MediaPanel, MediaPanel];
  clues: Clue[];
}

// ─── Round data ───────────────────────────────────────────────────────────────

const ROUNDS: Round[] = [
  {
    id: 1,
    title: 'The Job Offer',
    context: 'Image — Recruiter Profile',
    scenario: 'A student received a job offer from a recruiter whose LinkedIn profile photo looked off. Fraud investigators flagged it as AI-generated. One of these two profile photos is real. One was synthesised to run an employment scam.',
    forensicConcept: 'Hand Geometry Artifacts',
    forensicExplainer: 'AI face generators are trained on billions of face-forward photos but have very limited training data for hands. The complex joint geometry of fingers confuses the model — it frequently generates extra digits, fused fingers, or asymmetric knuckle positioning. Always check the hands.',
    panels: [
      {
        id: 'A',
        type: 'image',
        title: 'Profile — Sarah M.',
        description: 'Professional headshot, neutral background, natural skin tones.',
        isFake: false,
        artifactMarkers: [],
        visualNote: 'Natural proportions. Consistent lighting from upper left. Hair strands visible at edges.',
      },
      {
        id: 'B',
        type: 'image',
        title: 'Profile — Alex T.',
        description: 'Professional headshot, soft studio background. One hand partially visible at frame edge.',
        isFake: true,
        artifactMarkers: [
          { top: '68%', left: '74%', label: '6 fingers' },
          { top: '75%', left: '62%', label: 'Fused knuckle' },
        ],
        visualNote: 'Background gradient unnaturally smooth. Earlobes appear asymmetric.',
      },
    ],
    clues: [
      { id: 'c1a', text: 'The background is slightly out of focus', isCorrect: false },
      { id: 'c1b', text: 'The visible hand has 6 fingers and fused knuckles', isCorrect: true },
      { id: 'c1c', text: 'The lighting appears too even', isCorrect: false },
      { id: 'c1d', text: 'The subject is not smiling', isCorrect: false },
    ],
  },
  {
    id: 2,
    title: 'The Principal\'s Voice',
    context: 'Audio — Voice Message',
    scenario: 'A student reported receiving a threatening voice message that sounded like their school principal. The principal denied ever sending it. Audio forensics was called in. Which waveform is the authentic recording?',
    forensicConcept: 'Prosody Flattening',
    forensicExplainer: 'Human speech contains thousands of micro-variations per second — breath pauses, pitch drift, natural hesitations, and subtle timing irregularities. Text-to-speech and voice cloning systems average these out, producing speech that is too rhythmically regular. A real speaker never speaks in perfectly metered intervals.',
    panels: [
      {
        id: 'A',
        type: 'audio',
        title: 'Recording A — 14 seconds',
        description: 'Voice message, school hallway ambient noise in background.',
        isFake: false,
        artifactMarkers: [],
        visualNote: 'Waveform shows natural amplitude variation. Breath pause at 4s and 9s. Trailing off at sentence ends.',
      },
      {
        id: 'B',
        type: 'audio',
        title: 'Recording B — 14 seconds',
        description: 'Voice message, clean audio, no background noise.',
        isFake: true,
        artifactMarkers: [
          { top: '30%', left: '25%', label: 'No breath pause' },
          { top: '30%', left: '58%', label: 'Uniform amplitude' },
          { top: '30%', left: '82%', label: 'Clipped ending' },
        ],
        visualNote: 'Waveform amplitude is suspiciously consistent throughout. No breathing detected between sentences.',
      },
    ],
    clues: [
      { id: 'c2a', text: 'Recording B has no background noise', isCorrect: false },
      { id: 'c2b', text: 'Recording B sounds too clear for a hallway', isCorrect: false },
      { id: 'c2c', text: 'Recording B has no breath pauses and unnaturally uniform volume throughout', isCorrect: true },
      { id: 'c2d', text: 'The principal\'s voice sounds slightly different', isCorrect: false },
    ],
  },
  {
    id: 3,
    title: 'The Protest Photo',
    context: 'Image — News Photograph',
    scenario: 'This photo was shared on social media claiming to show a large protest outside a government building. Before sharing it, a fact-checker ran forensic analysis. One image is authentic photojournalism. One is a composite assembled from multiple photos.',
    forensicConcept: 'Shadow Direction Inconsistency',
    forensicExplainer: 'Every real photograph has a single, consistent light source. When images are composited — people pasted from one photo into another — each pasted element retains its original shadow direction. A crowd where half the people cast shadows left and the other half cast shadows right is physically impossible under a single sun.',
    panels: [
      {
        id: 'A',
        type: 'image',
        title: 'Photo A — Reuters, timestamped',
        description: 'Wide-angle crowd shot, afternoon lighting, consistent shadow direction.',
        isFake: false,
        artifactMarkers: [],
        visualNote: 'All visible shadows fall to the lower-right. Consistent with 3pm sunlight from upper-left. Crowd depth and scale consistent.',
      },
      {
        id: 'B',
        type: 'image',
        title: 'Photo B — Anonymous upload',
        description: 'Wide-angle crowd shot, similar composition.',
        isFake: true,
        artifactMarkers: [
          { top: '55%', left: '20%', label: 'Shadow: left' },
          { top: '55%', left: '55%', label: 'Shadow: right' },
          { top: '70%', left: '38%', label: 'Scale mismatch' },
        ],
        visualNote: 'Foreground figures cast shadows to the left. Background figures cast shadows to the right. Impossible under one sun.',
      },
    ],
    clues: [
      { id: 'c3a', text: 'The crowd in Photo B appears larger than is realistic', isCorrect: false },
      { id: 'c3b', text: 'Photo B was uploaded anonymously with no timestamp', isCorrect: false },
      { id: 'c3c', text: 'Figures in Photo B cast shadows in two opposite directions simultaneously', isCorrect: true },
      { id: 'c3d', text: 'The building in Photo B looks digitally smoothed', isCorrect: false },
    ],
  },
  {
    id: 4,
    title: 'The CEO Confession',
    context: 'Video — Facial Animation',
    scenario: 'A 40-second video appeared online showing a major tech CEO appearing to admit to financial fraud. The company\'s legal team commissioned deepfake forensics. Study both video thumbnails carefully — one shows a real recording, one is synthesised.',
    forensicConcept: 'Mouth Interior Coherence',
    forensicExplainer: 'Current deepfake face-swap models are trained primarily on external facial features. The interior of the mouth — the relationship between upper teeth, lower teeth, tongue position, and the soft palate — is rarely included in training data. During speech, deepfake teeth frequently flicker, merge, or vanish between frames.',
    panels: [
      {
        id: 'A',
        type: 'video',
        title: 'Video A — Verified press conference',
        description: 'Recorded at shareholder meeting, multiple camera angles available.',
        isFake: false,
        artifactMarkers: [],
        visualNote: 'Tooth and tongue movement anatomically consistent frame-to-frame. Natural blink rate (14–17 per minute). Micro-expressions visible.',
      },
      {
        id: 'B',
        type: 'video',
        title: 'Video B — Anonymous leak',
        description: 'Single camera, indoor setting, no verifiable timestamp.',
        isFake: true,
        artifactMarkers: [
          { top: '52%', left: '44%', label: 'Teeth flicker' },
          { top: '62%', left: '52%', label: 'Lower lip merge' },
          { top: '40%', left: '30%', label: 'Blink: 0 in 30s' },
        ],
        visualNote: 'Lower teeth disappear mid-word. Blink rate is 0 over a 30-second span — anatomically impossible. Jaw movement does not match audio timing.',
      },
    ],
    clues: [
      { id: 'c4a', text: 'The lighting in Video B looks artificial', isCorrect: false },
      { id: 'c4b', text: 'The CEO in Video B does not blink once in 30 seconds, and teeth disappear mid-word', isCorrect: true },
      { id: 'c4c', text: 'Video B was uploaded by an anonymous account', isCorrect: false },
      { id: 'c4d', text: 'The audio in Video B sounds slightly distorted', isCorrect: false },
    ],
  },
  {
    id: 5,
    title: 'The Alibi',
    context: 'Image — Metadata Forensics',
    scenario: 'A person posted a photo on social media claiming to prove they attended a concert on a specific date, establishing an alibi. A detective noticed something invisible to the naked eye. Both photos look identical to the human eye — but only one was genuinely taken on the claimed date.',
    forensicConcept: 'EXIF Timestamp Forgery',
    forensicExplainer: 'Every digital photo contains hidden EXIF metadata — a block of data recording the camera model, GPS coordinates, focal length, and critically, the exact timestamp the shutter was pressed. AI-generated images embed a creation timestamp corresponding to when they were synthesised, not a claimed event date. This metadata survives most social media uploads and cannot be faked without specialist tools that leave their own traces.',
    panels: [
      {
        id: 'A',
        type: 'image',
        title: 'Photo A — Posted Saturday 22 March',
        description: 'Concert crowd photo, stage lighting, consistent with the venue\'s known layout.',
        isFake: false,
        artifactMarkers: [],
        visualNote: 'EXIF data: Created 22 Mar 2025, 21:47. GPS: matches venue coordinates. Camera: iPhone 15 Pro. No editing software detected.',
      },
      {
        id: 'B',
        type: 'image',
        title: 'Photo B — Posted Saturday 22 March',
        description: 'Nearly identical concert crowd photo, similar composition.',
        isFake: true,
        artifactMarkers: [
          { top: '15%', left: '10%', label: 'Created: 2 Mar — 20 days early' },
          { top: '25%', left: '10%', label: 'No GPS data' },
          { top: '35%', left: '10%', label: 'Software: Stable Diffusion' },
        ],
        visualNote: 'EXIF data: Created 2 Mar 2025, 14:22 — 20 days before the concert. No GPS coordinates. Software field reads "Stable Diffusion XL".',
      },
    ],
    clues: [
      { id: 'c5a', text: 'Photo B has slightly different crowd density', isCorrect: false },
      { id: 'c5b', text: 'Photo B looks too professionally composed for a phone shot', isCorrect: false },
      { id: 'c5c', text: 'Photo B\'s EXIF metadata shows it was created 20 days before the concert by AI image software', isCorrect: true },
      { id: 'c5d', text: 'The stage lighting in Photo B looks wrong', isCorrect: false },
    ],
  },
];

const RANKS = [
  { label: 'Rookie', min: 0,   max: 40,  color: '#64748b', description: 'Keep practising — deepfakes are getting harder to spot. Review the forensic techniques.' },
  { label: 'Agent',  min: 41,  max: 70,  color: '#f59e0b', description: 'Solid instincts. You caught most of them but some techniques still caught you out.' },
  { label: 'Forensic Expert', min: 71, max: 100, color: '#10b981', description: 'Exceptional. You applied forensic reasoning that most people miss entirely.' },
];

const MODULE_ID = 'deepfake-detective';
const XP_CORRECT_ID = 20;
const XP_CORRECT_CLUE = 20;
const XP_COMPLETION = 100;

// ─── Helper components ────────────────────────────────────────────────────────

function MediaCard({
  panel,
  selected,
  revealed,
  onSelect,
}: {
  panel: MediaPanel;
  selected: boolean;
  revealed: boolean;
  onSelect: () => void;
}) {
  const typeColor = panel.type === 'image' ? '#6366f1' : panel.type === 'audio' ? '#06b6d4' : '#f59e0b';
  const typeLabel = panel.type === 'image' ? 'IMAGE' : panel.type === 'audio' ? 'AUDIO' : 'VIDEO';

  const border = revealed
    ? panel.isFake
      ? '2px solid #ef4444'
      : '2px solid #10b981'
    : selected
    ? '2px solid #a78bfa'
    : '2px solid #1e293b';

  const bg = revealed
    ? panel.isFake
      ? 'rgba(239,68,68,0.06)'
      : 'rgba(16,185,129,0.06)'
    : selected
    ? 'rgba(167,139,250,0.08)'
    : 'rgba(15,23,42,0.6)';

  return (
    <div
      onClick={!revealed ? onSelect : undefined}
      style={{
        border, background: bg, borderRadius: 12, padding: 16,
        cursor: revealed ? 'default' : 'pointer',
        transition: 'all 0.25s ease',
        position: 'relative' as const,
        flex: 1,
      }}
    >
      {/* Type badge */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ fontSize: 10, color: typeColor, fontWeight: 700, letterSpacing: '0.1em' }}>{typeLabel}</span>
        {revealed && (
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 100,
            background: panel.isFake ? 'rgba(239,68,68,0.15)' : 'rgba(16,185,129,0.15)',
            color: panel.isFake ? '#ef4444' : '#10b981',
          }}>
            {panel.isFake ? 'FAKE' : 'REAL'}
          </span>
        )}
      </div>

      {/* Media illustration area */}
      <div style={{
        background: '#0a0e1a',
        border: '1px solid #1e293b',
        borderRadius: 8,
        padding: '12px',
        marginBottom: 10,
        minHeight: 100,
        position: 'relative' as const,
        overflow: 'hidden',
      }}>
        {/* Visual representation based on type */}
        {panel.type === 'audio' ? (
          <AudioWaveform isFake={panel.isFake} revealed={revealed} />
        ) : panel.type === 'video' ? (
          <VideoFrame description={panel.description} />
        ) : (
          <ImageFrame description={panel.description} />
        )}

        {/* Artifact markers — only shown after reveal */}
        {revealed && panel.artifactMarkers.map((m, i) => (
          <div key={i} style={{
            position: 'absolute' as const, top: m.top, left: m.left,
            background: 'rgba(239,68,68,0.9)', color: '#fff',
            fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
            whiteSpace: 'nowrap' as const, pointerEvents: 'none' as const,
            border: '1px solid rgba(239,68,68,0.5)',
          }}>
            {m.label}
          </div>
        ))}
      </div>

      <div style={{ fontSize: 13, color: '#e2e8f0', fontWeight: 600, marginBottom: 4 }}>{panel.title}</div>
      <div style={{ fontSize: 11.5, color: '#64748b', lineHeight: 1.5, marginBottom: revealed ? 8 : 0 }}>{panel.description}</div>

      {revealed && (
        <div style={{ fontSize: 11, color: panel.isFake ? '#fca5a5' : '#6ee7b7', lineHeight: 1.55, background: panel.isFake ? 'rgba(239,68,68,0.08)' : 'rgba(16,185,129,0.08)', borderRadius: 6, padding: '8px 10px' }}>
          {panel.visualNote}
        </div>
      )}

      {!revealed && selected && (
        <div style={{ position: 'absolute' as const, top: 10, right: 10, width: 20, height: 20, borderRadius: '50%', background: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#fff', fontWeight: 700 }}>
          ?
        </div>
      )}
    </div>
  );
}

function AudioWaveform({ isFake, revealed }: { isFake: boolean; revealed: boolean }) {
  const bars = Array.from({ length: 32 }, (_, i) => {
    if (isFake) {
      // Uniform height for fake — suspiciously consistent
      return 40 + Math.sin(i * 0.3) * 8;
    }
    // Natural variation for real
    return 20 + Math.abs(Math.sin(i * 1.7 + 1.2) * 55) + (i % 7 === 0 ? 0 : 15);
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 2, height: 60, padding: '4px 0' }}>
      {bars.map((h, i) => (
        <div key={i} style={{
          flex: 1, height: `${h}%`, borderRadius: 2,
          background: revealed && isFake && (i > 8 && i < 24) ? '#ef444480' : '#334155',
          transition: 'background 0.3s',
        }} />
      ))}
    </div>
  );
}

function ImageFrame({ description }: { description: string }) {
  return (
    <div style={{ height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' as const }}>
        <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg,#334155,#1e293b)', margin: '0 auto 6px', border: '2px solid #475569' }} />
        <div style={{ fontSize: 10, color: '#475569', maxWidth: 160 }}>{description}</div>
      </div>
    </div>
  );
}

function VideoFrame({ description }: { description: string }) {
  return (
    <div style={{ height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a', borderRadius: 4 }}>
      <div style={{ textAlign: 'center' as const }}>
        <div style={{ fontSize: 20, marginBottom: 4, color: '#475569' }}>[VIDEO]</div>
        <div style={{ fontSize: 10, color: '#475569', maxWidth: 160 }}>{description}</div>
      </div>
    </div>
  );
}

// ─── Screens ──────────────────────────────────────────────────────────────────

type Screen = 'intro' | 'playing' | 'report';

export default function DeepfakeDetective() {
  const navigate = useNavigate();
  const [screen, setScreen] = useState<Screen>('intro');
  const [roundIndex, setRoundIndex] = useState(0);
  const [selectedPanel, setSelectedPanel] = useState<'A' | 'B' | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [selectedClue, setSelectedClue] = useState<string | null>(null);
  const [clueConfirmed, setClueConfirmed] = useState(false);
  const [results, setResults] = useState<{ correct: boolean; correctClue: boolean }[]>([]);
  const [sessionXP, setSessionXP] = useState(0);

  const round = ROUNDS[roundIndex];

  function handleReveal() {
    if (!selectedPanel) return;
    setRevealed(true);
    const correct = round.panels.find(p => p.id === selectedPanel)?.isFake === true;
    if (correct) {
      const updated = addXP(XP_CORRECT_ID);
      setSessionXP(p => p + XP_CORRECT_ID);
      syncProgress(MODULE_ID, updated.total);
    }
  }

  function handleClueConfirm() {
    if (!selectedClue) return;
    const clue = round.clues.find(c => c.id === selectedClue);
    const correctClue = clue?.isCorrect === true;
    if (correctClue) {
      const updated = addXP(XP_CORRECT_CLUE);
      setSessionXP(p => p + XP_CORRECT_CLUE);
      syncProgress(MODULE_ID, updated.total);
    }
    const correct = round.panels.find(p => p.id === selectedPanel)?.isFake === true;
    setResults(prev => [...prev, { correct, correctClue }]);
    setClueConfirmed(true);
  }

  function handleNext() {
    if (roundIndex < ROUNDS.length - 1) {
      setRoundIndex(roundIndex + 1);
      setSelectedPanel(null);
      setRevealed(false);
      setSelectedClue(null);
      setClueConfirmed(false);
    } else {
      addXP(XP_COMPLETION, MODULE_ID);
      recordStart();
      setSessionXP(p => p + XP_COMPLETION);
      syncProgress(MODULE_ID, loadXP().total);
      setScreen('report');
    }
  }

  const correctCount = results.filter(r => r.correct).length;
  const accuracy = results.length > 0 ? Math.round((correctCount / results.length) * 100) : 0;
  const rank = RANKS.filter(r => accuracy >= r.min).pop()!;

  return (
    <div style={{ minHeight: '100vh', background: '#0a0e1a', color: '#e2e8f0', paddingTop: 64 }}>
      <style>{`
        @keyframes fadeIn { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
        @keyframes slideIn { from { opacity:0; transform:translateX(-12px); } to { opacity:1; transform:translateX(0); } }
        @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
      `}</style>

      <div style={{ maxWidth: 600, margin: '0 auto', padding: '20px 16px 48px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 10, color: '#6366f1', letterSpacing: '0.15em', textTransform: 'uppercase' as const }}>PUZZLE · FORENSIC</div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#f8fafc', lineHeight: 1.2, marginTop: 2 }}>
              Deepfake Detective
            </h1>
          </div>
          <div style={{ textAlign: 'right' as const }}>
            {screen === 'playing' && (
              <>
                <div style={{ fontSize: 11, color: '#64748b' }}>ROUND</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#6366f1' }}>{roundIndex + 1} / {ROUNDS.length}</div>
              </>
            )}
            <div style={{ fontSize: 10, color: '#ffa502' }}>+{sessionXP} XP</div>
          </div>
        </div>

        {/* ── Intro ── */}
        {screen === 'intro' && (
          <div style={{ animation: 'fadeIn 0.6s ease' }}>
            <div style={{ background: 'linear-gradient(135deg,rgba(99,102,241,0.08),rgba(168,85,247,0.08))', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 16, padding: '28px 24px', marginBottom: 24, position: 'relative' as const, overflow: 'hidden' }}>
              <div style={{ position: 'absolute' as const, top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg,transparent,#6366f1,transparent)', animation: 'pulse 2s infinite' }} />
              <h2 style={{ fontSize: 20, fontWeight: 700, color: '#f1f5f9', marginBottom: 12 }}>
                Can you spot what the human eye misses?
              </h2>
              <p style={{ fontSize: 14, color: '#94a3b8', lineHeight: 1.7, marginBottom: 8 }}>
                Deepfakes — AI-generated or AI-manipulated media — are used to spread disinformation, run fraud, fabricate evidence, and manufacture fake alibis.
              </p>
              <p style={{ fontSize: 14, color: '#94a3b8', lineHeight: 1.7, marginBottom: 8 }}>
                In each round you will be shown two pieces of media. One is real. One is synthetic. Your job is to identify the fake — then select the forensic clue that proves it.
              </p>
              <p style={{ fontSize: 14, color: '#6366f1', lineHeight: 1.7 }}>
                5 rounds. Escalating difficulty. Each round teaches a new forensic technique used by real investigators.
              </p>
            </div>

            <div style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid #1e293b', borderRadius: 12, padding: '16px 20px', marginBottom: 24 }}>
              <div style={{ fontSize: 11, color: '#475569', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.08em', marginBottom: 12 }}>Scoring</div>
              {[
                { label: 'Correct fake identified', xp: '+20 XP' },
                { label: 'Correct forensic clue selected', xp: '+20 XP' },
                { label: 'Module completion bonus', xp: '+100 XP' },
              ].map((s, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: i < 2 ? '1px solid #1e293b' : 'none' }}>
                  <span style={{ fontSize: 13, color: '#cbd5e1' }}>{s.label}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#ffa502' }}>{s.xp}</span>
                </div>
              ))}
            </div>

            <button
              onClick={() => setScreen('playing')}
              style={{ width: '100%', padding: 16, borderRadius: 12, background: 'linear-gradient(135deg,#6366f1,#7c3aed)', border: 'none', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}
            >
              Begin Investigation
            </button>
            <button
              onClick={() => navigate('/')}
              style={{ width: '100%', marginTop: 10, padding: 12, background: 'none', border: '1px solid #1e293b', borderRadius: 10, color: '#475569', fontSize: 14, cursor: 'pointer' }}
            >
              Back to Dashboard
            </button>
          </div>
        )}

        {/* ── Playing ── */}
        {screen === 'playing' && (
          <div style={{ animation: 'fadeIn 0.4s ease' }}>
            {/* Round progress dots */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
              {ROUNDS.map((r, i) => (
                <div key={i} style={{
                  flex: 1, height: 4, borderRadius: 2,
                  background: i < roundIndex ? '#6366f1' : i === roundIndex ? '#a78bfa' : '#1e293b',
                  transition: 'background 0.3s',
                }} />
              ))}
            </div>

            {/* Round header */}
            <div style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid #1e293b', borderRadius: 12, padding: '18px 20px', marginBottom: 16 }}>
              <div style={{ fontSize: 10, color: '#6366f1', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' as const, marginBottom: 4 }}>
                Round {round.id} · {round.context}
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#f1f5f9', marginBottom: 8 }}>{round.title}</div>
              <p style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.65, margin: 0 }}>{round.scenario}</p>
            </div>

            {/* Media panels */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              {round.panels.map(panel => (
                <MediaCard
                  key={panel.id}
                  panel={panel}
                  selected={selectedPanel === panel.id}
                  revealed={revealed}
                  onSelect={() => !revealed && setSelectedPanel(panel.id)}
                />
              ))}
            </div>

            {/* Identify button */}
            {!revealed && (
              <button
                onClick={handleReveal}
                disabled={!selectedPanel}
                style={{
                  width: '100%', padding: 14, borderRadius: 10,
                  background: selectedPanel ? 'rgba(239,68,68,0.15)' : 'rgba(30,41,59,0.5)',
                  border: selectedPanel ? '1px solid rgba(239,68,68,0.35)' : '1px solid #1e293b',
                  color: selectedPanel ? '#ef4444' : '#334155',
                  fontSize: 14, fontWeight: 600, cursor: selectedPanel ? 'pointer' : 'not-allowed',
                  marginBottom: 8,
                }}
              >
                {selectedPanel ? `That one is the fake — reveal` : 'Select which you think is fake'}
              </button>
            )}

            {/* Result + clue selection */}
            {revealed && (
              <div style={{ animation: 'fadeIn 0.35s ease' }}>
                {/* Correct / wrong feedback */}
                <div style={{
                  padding: '12px 16px', borderRadius: 10, marginBottom: 16,
                  background: round.panels.find(p => p.id === selectedPanel)?.isFake
                    ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)',
                  border: `1px solid ${round.panels.find(p => p.id === selectedPanel)?.isFake ? 'rgba(16,185,129,0.25)' : 'rgba(239,68,68,0.25)'}`,
                }}>
                  <div style={{
                    fontSize: 13, fontWeight: 700,
                    color: round.panels.find(p => p.id === selectedPanel)?.isFake ? '#10b981' : '#ef4444',
                    marginBottom: 4,
                  }}>
                    {round.panels.find(p => p.id === selectedPanel)?.isFake ? 'Correct — that was the fake.' : 'Incorrect — you selected the real one.'}
                  </div>
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>
                    Now select the forensic clue that proves it.
                  </div>
                </div>

                {/* Clue selection */}
                {!clueConfirmed && (
                  <>
                    <div style={{ fontSize: 11, color: '#475569', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.08em', marginBottom: 10 }}>
                      What is the evidence? Select one:
                    </div>
                    {round.clues.map((clue, i) => (
                      <div
                        key={clue.id}
                        onClick={() => setSelectedClue(clue.id)}
                        style={{
                          padding: '12px 14px', borderRadius: 9, marginBottom: 8, cursor: 'pointer',
                          border: selectedClue === clue.id ? '1px solid #6366f1' : '1px solid #1e293b',
                          background: selectedClue === clue.id ? 'rgba(99,102,241,0.1)' : 'rgba(15,23,42,0.6)',
                          animation: `slideIn 0.3s ease ${i * 0.07}s both`,
                        }}
                      >
                        <span style={{ fontSize: 13, color: selectedClue === clue.id ? '#a5b4fc' : '#94a3b8' }}>{clue.text}</span>
                      </div>
                    ))}
                    <button
                      onClick={handleClueConfirm}
                      disabled={!selectedClue}
                      style={{
                        width: '100%', padding: 13, borderRadius: 10, marginTop: 4,
                        background: selectedClue ? 'linear-gradient(135deg,#6366f1,#7c3aed)' : '#1e293b',
                        border: 'none', color: selectedClue ? '#fff' : '#334155',
                        fontSize: 14, fontWeight: 600, cursor: selectedClue ? 'pointer' : 'not-allowed',
                      }}
                    >
                      Confirm clue selection
                    </button>
                  </>
                )}

                {/* Forensic explainer after clue confirmed */}
                {clueConfirmed && (
                  <div style={{ animation: 'fadeIn 0.4s ease' }}>
                    <div style={{ padding: '14px 16px', borderRadius: 10, marginBottom: 16, background: round.clues.find(c => c.id === selectedClue)?.isCorrect ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)', border: `1px solid ${round.clues.find(c => c.id === selectedClue)?.isCorrect ? 'rgba(16,185,129,0.25)' : 'rgba(239,68,68,0.25)'}` }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: round.clues.find(c => c.id === selectedClue)?.isCorrect ? '#10b981' : '#ef4444', marginBottom: 6 }}>
                        {round.clues.find(c => c.id === selectedClue)?.isCorrect ? 'Correct clue.' : `Incorrect. The right answer: "${round.clues.find(c => c.isCorrect)?.text}"`}
                      </div>
                    </div>

                    <div style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 12, padding: '16px 18px', marginBottom: 16 }}>
                      <div style={{ fontSize: 10, color: '#6366f1', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.1em', marginBottom: 8 }}>
                        Forensic Technique Unlocked: {round.forensicConcept}
                      </div>
                      <p style={{ fontSize: 13, color: '#cbd5e1', lineHeight: 1.7, margin: 0 }}>{round.forensicExplainer}</p>
                    </div>

                    <button
                      onClick={handleNext}
                      style={{
                        width: '100%', padding: 14, borderRadius: 10,
                        background: roundIndex < ROUNDS.length - 1
                          ? 'linear-gradient(135deg,rgba(99,102,241,0.3),rgba(99,102,241,0.15))'
                          : 'linear-gradient(135deg,#6366f1,#7c3aed)',
                        border: roundIndex < ROUNDS.length - 1 ? '1px solid rgba(99,102,241,0.3)' : 'none',
                        color: '#f1f5f9', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                      }}
                    >
                      {roundIndex < ROUNDS.length - 1 ? `Next round: ${ROUNDS[roundIndex + 1].title}` : 'View Forensic Report Card'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Report Card ── */}
        {screen === 'report' && (
          <div style={{ animation: 'fadeIn 0.6s ease' }}>
            <div style={{ textAlign: 'center' as const, marginBottom: 28 }}>
              <div style={{ fontSize: 11, color: rank.color, letterSpacing: '0.15em', textTransform: 'uppercase' as const, marginBottom: 8, fontWeight: 700 }}>
                FORENSIC REPORT CARD
              </div>
              <h2 style={{ fontSize: 26, color: '#f1f5f9', fontWeight: 700, margin: 0 }}>Investigation Complete</h2>
            </div>

            {/* Rank badge */}
            <div style={{ background: `${rank.color}11`, border: `1px solid ${rank.color}33`, borderRadius: 16, padding: '24px 20px', textAlign: 'center' as const, marginBottom: 24 }}>
              <div style={{ fontSize: 48, fontWeight: 800, color: rank.color, lineHeight: 1 }}>{accuracy}%</div>
              <div style={{ fontSize: 13, color: '#64748b', marginBottom: 10 }}>Detection accuracy</div>
              <div style={{ display: 'inline-block', background: `${rank.color}22`, border: `1px solid ${rank.color}44`, borderRadius: 100, padding: '4px 18px', fontSize: 13, fontWeight: 700, color: rank.color }}>
                {rank.label}
              </div>
              <p style={{ fontSize: 13, color: '#94a3b8', marginTop: 12, lineHeight: 1.6 }}>{rank.description}</p>
            </div>

            {/* Round-by-round breakdown */}
            <div style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid #1e293b', borderRadius: 12, padding: '16px 18px', marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: '#475569', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.08em', marginBottom: 14 }}>Round breakdown</div>
              {ROUNDS.map((r, i) => {
                const res = results[i];
                return (
                  <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 0', borderBottom: i < ROUNDS.length - 1 ? '1px solid #0f172a' : 'none' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, color: '#cbd5e1', fontWeight: 500 }}>{r.title}</div>
                      <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>{r.forensicConcept}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 6, fontWeight: 700, background: res?.correct ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)', color: res?.correct ? '#10b981' : '#ef4444' }}>
                        {res?.correct ? 'Fake found' : 'Missed'}
                      </span>
                      <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 6, fontWeight: 700, background: res?.correctClue ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)', color: res?.correctClue ? '#10b981' : '#ef4444' }}>
                        {res?.correctClue ? 'Clue correct' : 'Clue wrong'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Techniques mastered */}
            <div style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.15)', borderRadius: 12, padding: '16px 18px', marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: '#6366f1', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.08em', marginBottom: 12 }}>
                Forensic Techniques Covered
              </div>
              {ROUNDS.map((r, i) => (
                <div key={r.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '7px 0', borderBottom: i < ROUNDS.length - 1 ? '1px solid rgba(99,102,241,0.08)' : 'none' }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: results[i]?.correctClue ? '#6366f1' : '#334155', marginTop: 5, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 12, color: results[i]?.correctClue ? '#a5b4fc' : '#475569', fontWeight: 600 }}>{r.forensicConcept}</div>
                    <div style={{ fontSize: 11, color: '#334155' }}>{results[i]?.correctClue ? 'Mastered' : 'Review recommended'}</div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 12, padding: '16px 18px', textAlign: 'center' as const, marginBottom: 16 }}>
              <div style={{ color: '#10b981', fontSize: 16, fontWeight: 700 }}>Truth Seeker badge earned</div>
              <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>5 rounds completed · {XP_COMPLETION} XP bonus awarded</div>
              <div style={{ color: '#ffa502', fontSize: 14, fontWeight: 700, marginTop: 8 }}>+{sessionXP} XP total this session</div>
            </div>

            <ModuleSummary moduleId="detective" xpEarned={sessionXP} />
          </div>
        )}
      </div>
    </div>
  );
}
